import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Calendar, ArrowRight, Share2, Bookmark, Heart, Eye, Loader2 } from 'lucide-react';
import '../../../assests/css/customer/BlogDetail.css';
import MyNavbar from '../../../components/Customer/Mynavbar/MyNavbar';
import Footer from '../../../components/Customer/Footer/Footer';
import AppContext from '../../../context/AppContext';
import axios from 'axios';

const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { baseUrl, baseUrlForFiles } = useContext(AppContext);

  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    setError(null);

    // Single blog fetch
    axios.get(`${baseUrl}/admin/blog/getBlog/${id}`)
      .then((res) => {
        if (res.data.success) {
          setArticle(res.data.data);

          // Related blogs fetch
          axios.get(`${baseUrl}/admin/blog/getAllBlogs`)
            .then((allRes) => {
              if (allRes.data.success) {
                const related = allRes.data.data
                  .filter(b => b.id !== res.data.data.id)
                  .sort(() => 0.5 - Math.random())
                  .slice(0, 3);
                setRelatedArticles(related);
              }
            })
            .catch(() => {});
        } else {
          setError('Blog nahi mila.');
        }
      })
      .catch(() => setError('Server se connection nahi ho raha.'))
      .finally(() => setLoading(false));
  }, [id]);

  const getImageUrl = (img) => {
    if (!img) return 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1200&h=600&fit=crop&q=80';
    if (img.startsWith('http')) return img;
    return `${baseUrlForFiles}/${img}`;
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  if (loading) {
    return (
      <>
        <MyNavbar />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', gap: '12px' }}>
          <Loader2 style={{ animation: 'spin 1s linear infinite', width: 28, height: 28 }} />
          <span style={{ fontSize: '16px', color: 'var(--color-text-secondary)' }}>Blog load ho raha hai...</span>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !article) {
    return (
      <>
        <MyNavbar />
        <div style={{ textAlign: 'center', padding: '80px 20px' }}>
          <p style={{ fontSize: '18px', color: 'var(--color-text-secondary)' }}>{error || 'Blog nahi mila.'}</p>
          <button onClick={() => navigate('/blog')} style={{ marginTop: '20px', padding: '10px 24px', borderRadius: '8px', cursor: 'pointer' }}>
            Back to Blog
          </button>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <MyNavbar />
      <div className="detail-page">

        {/* HERO HEADER */}
        <header className="detail-hero">
          <div className="detail-hero-bg"></div>
          <div className="detail-container">
            <button className="detail-back-btn" onClick={() => navigate('/blog')}>
              <ArrowLeft className="detail-icon" />
              <span>Back to Blog</span>
            </button>

            <div className="detail-hero-content">
              <span className="detail-category">{article.mlcategory}</span>
              <h1 className="detail-hero-title">{article.title}</h1>
              <p className="detail-hero-excerpt">{article.excerpt}</p>

              <div className="detail-meta-row">
                <div className="detail-author">
                  {article.author_avatar && (
                    <img src={getImageUrl(article.author_avatar)} alt={article.author_name} className="detail-author-img" />
                  )}
                  <div className="detail-author-info">
                    <div className="detail-author-name">{article.author_name || 'Admin'}</div>
                    <div className="detail-author-role">{article.author_role || ''}</div>
                  </div>
                </div>

                <div className="detail-stats-row">
                  <div className="detail-stat">
                    <Calendar className="detail-stat-icon" />
                    <span>{formatDate(article.created_at)}</span>
                  </div>
                  <div className="detail-stat">
                    <Clock className="detail-stat-icon" />
                    <span>{article.read_time || '5 min read'}</span>
                  </div>
                  <div className="detail-stat">
                    <Eye className="detail-stat-icon" />
                    <span>{article.views || '0'}</span>
                  </div>
                  <div className="detail-stat">
                    <Heart className="detail-stat-icon" />
                    <span>{article.likes || '0'}</span>
                  </div>
                </div>
              </div>

              <div className="detail-actions">
                <button className="detail-action-btn detail-save">
                  <Bookmark className="detail-action-icon" />
                  <span>Save</span>
                </button>
                <button className="detail-action-btn detail-share">
                  <Share2 className="detail-action-icon" />
                  <span>Share</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* MAIN CONTENT */}
        <main className="detail-main">
          <div className="detail-container">
            <div className="detail-layout">

              {/* SIDEBAR */}
              <aside className="detail-sidebar">
                <div className="detail-sidebar-card">
                  {article.author_avatar && (
                    <img src={getImageUrl(article.author_avatar)} alt={article.author_name} className="detail-sidebar-avatar" />
                  )}
                  <h3 className="detail-sidebar-name">{article.author_name || 'Admin'}</h3>
                  <p className="detail-sidebar-role">{article.author_role || ''}</p>
                  <p className="detail-sidebar-bio">{article.author_bio || ''}</p>
                  <button className="detail-follow-btn">Follow</button>
                </div>

                <div className="detail-toc-card">
                  <h4 className="detail-toc-title">Table of Contents</h4>
                  <ul className="detail-toc-list">
                    <li className="detail-toc-item">Introduction</li>
                    <li className="detail-toc-item">Main Content</li>
                    <li className="detail-toc-item">Key Takeaways</li>
                    <li className="detail-toc-item">Conclusion</li>
                  </ul>
                </div>
              </aside>

              {/* ARTICLE CONTENT */}
              <article className="detail-content-wrapper">
                <img src={getImageUrl(article.image)} alt={article.title} className="detail-featured-img" />

                <div className="detail-article-content" dangerouslySetInnerHTML={{ __html: article.content }} />

                <div className="detail-cta-box">
                  <div className="detail-cta-icon">💌</div>
                  <h3 className="detail-cta-title">Get Weekly E-commerce Tips</h3>
                  <p className="detail-cta-text">Join 50,000+ entrepreneurs receiving actionable strategies</p>
                </div>

                <div className="detail-tags">
                  {article.tags
                    ? article.tags.split(',').map((tag, i) => <span key={i} className="detail-tag">{tag.trim()}</span>)
                    : <>
                        <span className="detail-tag">E-commerce</span>
                        <span className="detail-tag">Business Growth</span>
                        <span className="detail-tag">Online Store</span>
                      </>
                  }
                </div>
              </article>
            </div>
          </div>
        </main>

        {/* RELATED ARTICLES */}
        {relatedArticles.length > 0 && (
          <section className="detail-related">
            <div className="detail-container">
              <h2 className="detail-related-title">Continue Reading</h2>
              <div className="detail-related-grid">
                {relatedArticles.map((rel) => (
                  <article
                    key={rel.id}
                    className="detail-related-card"
                    onClick={() => { navigate(`/blog-detail/${rel.id}`); window.scrollTo(0, 0); }}
                  >
                    <div className="detail-related-img-wrapper">
                      <img src={getImageUrl(rel.image)} alt={rel.title} className="detail-related-img" />
                      <span className="detail-related-category">{rel.mlcategory}</span>
                    </div>
                    <div className="detail-related-body">
                      <h3 className="detail-related-card-title">{rel.title}</h3>
                      <p className="detail-related-excerpt">{rel.excerpt}</p>
                      <div className="detail-related-footer">
                        <span className="detail-related-time">{rel.read_time || '5 min'}</span>
                        <ArrowRight className="detail-related-arrow" />
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        <Footer />
      </div>
    </>
  );
};

export default BlogDetail;